"use client";

import { type FC, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Checkbox, H3 } from "@/shared/components";
import { displayFontClassName } from "@/lib/fonts";
import { cn } from "@/lib/utils";
import type { ProjectCategoryRef } from "@/lib/directus/utils/mapProjectCategories";
import type { BlockSectionBg } from "./common/types";
import {
  getSectionBorderClass,
  getSectionGridCellClass,
  getSectionGridDesktopColumnsClass,
  getSectionGridShellClass,
} from "./blockSection";
import { SectionGridRowFillers } from "./common/SectionGridRowFillers";
import {
  getProjectFilterCategories,
  projectMatchesSelectedCategories,
} from "./getProjectCategoriesFromItems";
import type { ProjectsTheme } from "./projectsTheme";
import type { ProjectItemType } from "./ProjectsBlock";
import { ProjectGridCard } from "./ProjectGridCard";

interface ProjectsFilteredGridProps {
  projects: ProjectItemType[];
  sectionBg: BlockSectionBg;
  theme: ProjectsTheme;
  serviceCategories: ProjectCategoryRef[];
}

const DESKTOP_COLUMNS = 3;

const filterCheckboxSizeClass =
  "h-8 w-8 desktop:h-4 desktop:w-4 [&_svg]:size-5 desktop:[&_svg]:size-4";

const filterOptionClass =
  "flex min-h-12 cursor-pointer items-center gap-4 py-2 desktop:min-h-0 desktop:py-0";

export const ProjectsFilteredGrid: FC<ProjectsFilteredGridProps> = ({
  projects,
  sectionBg,
  theme,
  serviceCategories,
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const borderClass = getSectionBorderClass(sectionBg);
  const gridCellClass = getSectionGridCellClass(sectionBg);
  const categories = useMemo(
    () => getProjectFilterCategories(projects, serviceCategories),
    [projects, serviceCategories]
  );

  const activeSelectedCategoryIds = useMemo(
    () =>
      selectedCategoryIds.filter((categoryId) =>
        categories.some((category) => category.id === categoryId)
      ),
    [selectedCategoryIds, categories]
  );

  const filteredProjects = useMemo(() => {
    return projects.filter((project) =>
      projectMatchesSelectedCategories(project, activeSelectedCategoryIds)
    );
  }, [projects, activeSelectedCategoryIds]);

  const handleCategoryToggle = (categoryId: string, checked: boolean) => {
    setSelectedCategoryIds((current) => {
      if (checked) {
        return current.includes(categoryId) ? current : [...current, categoryId];
      }
      return current.filter((value) => value !== categoryId);
    });
  };

  const itemsCount = filteredProjects.length;

  return (
    <div className="w-full min-w-0">
      {categories.length > 0 && (
        <div className={cn("border-b", borderClass)}>
          <button
            type="button"
            className={cn(
              "flex w-full items-center justify-between gap-4 px-4 py-6",
              "md:px-6 desktop:px-10 desktop:py-8",
              theme.filterText
            )}
            aria-expanded={isFilterOpen}
            onClick={() => setIsFilterOpen((current) => !current)}
          >
            <H3
              className={cn(
                displayFontClassName,
                "text-heading-3 font-bold leading-[1.2]",
                theme.filterText
              )}
            >
              Фильтр
            </H3>
            <ChevronDown
              className={cn(
                "size-6 shrink-0 transition-transform",
                theme.filterText,
                isFilterOpen && "rotate-180"
              )}
              aria-hidden
            />
          </button>

          {isFilterOpen && (
            <div
              className={cn(
                "flex flex-col gap-4 border-t px-4 py-6",
                "md:px-6 desktop:px-10 desktop:py-8",
                borderClass
              )}
            >
              {categories.map((category) => {
                const checkboxId = `project-category-${category.id}`;
                const isChecked = selectedCategoryIds.includes(category.id);

                return (
                  <label
                    key={category.id}
                    htmlFor={checkboxId}
                    className={cn(filterOptionClass, theme.filterText)}
                  >
                    <Checkbox
                      id={checkboxId}
                      checked={isChecked}
                      className={cn(filterCheckboxSizeClass, theme.filterCheckbox)}
                      onCheckedChange={(value) =>
                        handleCategoryToggle(category.id, value === true)
                      }
                    />
                    <span className="text-base leading-[1.2]">{category.title}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div
        className={cn(
          getSectionGridShellClass(sectionBg),
          "grid-cols-1 md:grid-cols-2",
          getSectionGridDesktopColumnsClass(DESKTOP_COLUMNS)
        )}
      >
        {filteredProjects.map((project) => (
          <div key={project.id} className={gridCellClass}>
            <ProjectGridCard project={project} theme={theme} />
          </div>
        ))}

        <SectionGridRowFillers
          itemsCount={itemsCount}
          desktopColumns={DESKTOP_COLUMNS}
          sectionBg={sectionBg}
        />
      </div>
    </div>
  );
};
